import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, CheckCircle2, FileText, Beaker, SkipForward, RotateCcw, Sparkles, ShieldCheck, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { api, formatApiError } from "../lib/api";

const STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const AGES = ["Under 50", "50-54", "55-59", "60-64", "65-69", "70-74", "75-79", "80+"];
const YEARS = (() => {
    const now = new Date().getFullYear();
    const arr = [];
    for (let y = now; y >= 1985; y--) arr.push(String(y));
    arr.push("Before 1985");
    return arr;
})();

const STEPS = ["Contact", "Annuity Details", "Features", "Your Needs", "Document", "Submit"];

const EMPTY_FORM = {
    full_name: "",
    email: "",
    phone: "",
    age: "",
    state: "",
    zip_code: "",
    annuity_type: "",
    company: "",
    current_value: "",
    purchase_year: "",
    income_rider: "",
    surrender_charge_known: "",
    goal: "",
    income_confidence: "",
    ltc_concern: "",
    currently_taking_income: "",
    financial_situation_changed: "",
    marital_status: "",
    spouse_has_annuity: "",
    review_focus: "",
    inquiry_type: "",
    file_url: "",
    file_name: "",
    agent_slug: "",
    lead_source: "",
    lead_source_detail: "",
};

const DEMO_SCENARIOS = {
    margaret: {
        label: "Margaret — Income Rider Trap",
        tagline: "Paying a rider fee she'll never use",
        data: {
            full_name: "Margaret Henderson",
            email: "margaret.henderson@example.com",
            phone: "(918) 555-0142",
            age: "65-69",
            state: "OK",
            zip_code: "74103",
            annuity_type: "Fixed Indexed",
            company: "Allianz",
            current_value: "385000",
            purchase_year: "2016",
            income_rider: "Yes",
            surrender_charge_known: "No",
            goal: "Income Later",
            income_confidence: "A little worried",
            ltc_concern: "Very concerned",
            currently_taking_income: "No — probably never",
            financial_situation_changed: "Somewhat",
            marital_status: "Yes",
            spouse_has_annuity: "Not sure",
            review_focus: "I've been paying an income rider fee for 8 years but I don't think I'll ever turn it on. Is this still the right product for me, or should I consider something else?",
        },
    },
    robert: {
        label: "Robert — Surrender Confusion",
        tagline: "Variable annuity, wants to withdraw, doesn't know his schedule",
        data: {
            full_name: "Robert Campbell",
            email: "r.campbell@example.com",
            phone: "(405) 555-0199",
            age: "60-64",
            state: "OK",
            zip_code: "73102",
            annuity_type: "Variable",
            company: "Prudential",
            current_value: "620000",
            purchase_year: "2018",
            income_rider: "Not Sure",
            surrender_charge_known: "No",
            goal: "Growth",
            income_confidence: "Mostly",
            ltc_concern: "Somewhat",
            currently_taking_income: "No — plan to later",
            financial_situation_changed: "A lot",
            marital_status: "No",
            spouse_has_annuity: "",
            review_focus: "My advisor said I could pull money out anytime but I've heard there are surrender charges. I need $80k for a business opportunity — is this the right account to pull from?",
        },
    },
    patricia: {
        label: "Patricia — Perfect Fit",
        tagline: "Already working. We'll tell her to keep it.",
        data: {
            full_name: "Patricia Brown",
            email: "p.brown@example.com",
            phone: "(405) 555-0178",
            age: "75-79",
            state: "OK",
            zip_code: "73103",
            annuity_type: "Fixed Indexed",
            company: "Athene",
            current_value: "195000",
            purchase_year: "2018",
            income_rider: "Yes",
            surrender_charge_known: "Yes",
            goal: "Income Now",
            income_confidence: "Very confident",
            ltc_concern: "Already planned for it",
            currently_taking_income: "Yes, I am",
            financial_situation_changed: "Not really",
            marital_status: "No",
            spouse_has_annuity: "",
            review_focus: "My son told me I should get a second opinion on this annuity. I like it, and the monthly income pays for most of my bills. I just want to make sure I'm not missing anything.",
        },
    },
};

export default function ReviewForm({ demoMode = false }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [scenario, setScenario] = useState("margaret");

    useEffect(() => {
        const slug = searchParams.get("agent");
        // Lead source: accept ?source=, ?utm_source=, or referer-based fallback. Persist in
        // sessionStorage so it survives the wizard even if the user reloads mid-flow.
        const urlSource = searchParams.get("source") || searchParams.get("utm_source");
        const urlSourceDetail = searchParams.get("source_detail") || searchParams.get("utm_campaign") || searchParams.get("utm_medium");
        let storedSource = "";
        let storedDetail = "";
        try {
            storedSource = sessionStorage.getItem("arp_lead_source") || "";
            storedDetail = sessionStorage.getItem("arp_lead_source_detail") || "";
        } catch { /* ignore */ }
        const finalSource = urlSource || storedSource || (slug ? "agent-page" : "");
        const finalDetail = urlSourceDetail || storedDetail || (slug ? slug : "");
        if (finalSource && finalSource !== storedSource) {
            try { sessionStorage.setItem("arp_lead_source", finalSource); } catch { /* ignore */ }
        }
        if (finalDetail && finalDetail !== storedDetail) {
            try { sessionStorage.setItem("arp_lead_source_detail", finalDetail); } catch { /* ignore */ }
        }
        setForm((f) => ({
            ...f,
            agent_slug: slug || f.agent_slug,
            lead_source: finalSource || f.lead_source,
            lead_source_detail: finalDetail || f.lead_source_detail,
        }));
    }, [searchParams]);

    // Demo toolbar (autofill / scenario switcher / jump-to-last-step) appears ONLY when
    // this component is mounted on the `/review-demo` route (demoMode prop = true).
    // The real `/review` route used by consumers NEVER shows demo controls — even when
    // an admin is signed in or `?demo=1` is in the URL.
    const isDemo = demoMode === true;

    const autofill = (jumpToReview = false) => {
        const scen = DEMO_SCENARIOS[scenario] || DEMO_SCENARIOS.margaret;
        setForm((f) => ({ ...f, ...scen.data }));
        setStep(jumpToReview ? STEPS.length - 1 : 0);
        toast.success(`${scen.label.split(" — ")[0]} loaded${jumpToReview ? " · jumped to review" : ""}`);
    };

    const resetForm = () => {
        const slug = searchParams.get("agent") || "";
        setForm({ ...EMPTY_FORM, agent_slug: slug });
        setStep(0);
        toast.success("Form cleared");
    };

    const validateStep = () => {
        if (step === 0) {
            if (!form.inquiry_type) return "Please tell us what we can help you with";
            if (!form.full_name.trim()) return "Full name is required";
            if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Valid email required";
            if (!form.phone.trim()) return "Phone is required";
            if (!form.age) return "Please select your age range";
            if (!form.state) return "Please select your state";
        }
        if (step === 1) {
            // Annuity type is only required when reviewing or adding to an existing contract.
            // First-time buyers, IRA→Roth seekers, CD replacers, and just-questions visitors
            // skip the hard requirement (they may not own one yet).
            const reviewingExisting = ["review-existing", "additional-purchase"].includes(form.inquiry_type);
            if (reviewingExisting && !form.annuity_type) return "Please select an annuity type";
        }
        return null;
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const next = () => {
        const err = validateStep();
        if (err) { toast.error(err); return; }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) { toast.error("File is too large (max 15MB)"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set("file_url", data.file_url);
            set("file_name", data.file_name);
            toast.success("File uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                current_value: form.current_value ? parseFloat(form.current_value) : null,
                is_demo: isDemo,  // backend skips persistence + emails when true
            };
            const { data } = await api.post("/submit-review", payload);
            navigate(isDemo ? "/booking?demo=1" : "/booking", { state: {
                scorecard: data.scorecard,
                leadId: data.id,
                name: form.full_name,
                email: form.email,
                phone: form.phone,
                advisor: data.assigned_agent_profile,
                isDemo,
            } });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative overflow-hidden bg-navy-900 min-h-screen py-16 md:py-24" data-testid="review-form-page">
            {/* Decorative background — matches the homepage hero language */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/70" />
                <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-amber-400/10 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-emerald-400/10 blur-3xl" />
            </div>

            <div className="relative max-w-3xl mx-auto px-6 sm:px-8">
                {isDemo && (
                    <div className="mb-6 bg-amber-400/15 border-2 border-amber-400/40 rounded-xl px-5 py-3 flex items-center justify-center gap-2 text-amber-200" data-testid="demo-route-banner">
                        <Beaker className="w-4 h-4" />
                        <span className="text-sm font-semibold">FMO Demo Mode</span>
                        <span className="text-xs opacity-80">— autofill enabled · submissions are not stored as real leads</span>
                    </div>
                )}
                <div className="mb-10 text-center">
                    <div className="inline-flex items-center gap-2 uppercase tracking-[0.25em] text-[11px] font-bold text-amber-300 mb-5 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                        <Sparkles className="w-3 h-3" /> Free Annuity Review · No Sales Pressure
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                        Tell us about <span className="text-amber-300">your annuity.</span>
                    </h1>
                    <p className="mt-5 text-lg text-blue-50/85">Takes about 3 minutes. No pressure. No spam.</p>
                    <p className="mt-2 text-sm text-blue-100/70">Already own one? Considering your first? Adding another? Same form. Same advisor.</p>
                    <div className="mt-6 max-w-2xl mx-auto bg-emerald-400/10 border border-emerald-400/30 rounded-xl px-5 py-4 text-emerald-100 text-left flex items-start gap-3" data-testid="dont-know-banner">
                        <Phone className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                        <p className="text-sm leading-relaxed">
                            <span className="font-semibold text-white">Don't have all the answers?</span> No problem.
                            If you only know your <span className="font-semibold text-white">insurance company and account number</span>, we'll get on the phone with them <em>together</em> and pull every detail you need for your review — at no cost to you.
                        </p>
                    </div>
                    <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-3 text-xs text-blue-100/85">
                        <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-md bg-emerald-400/20 text-emerald-300 flex items-center justify-center"><ShieldCheck className="w-3.5 h-3.5" /></span> Confidential & secure</div>
                        <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-md bg-amber-400/20 text-amber-300 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5" /></span> 100% free · no obligation</div>
                        <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-md bg-sky-400/20 text-sky-300 flex items-center justify-center"><FileText className="w-3.5 h-3.5" /></span> Written summary you keep</div>
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-8 md:p-12 relative overflow-visible">
                    {/* Top brand accent strip */}
                    <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl bg-gradient-to-r from-amber-400 via-rose-400 to-emerald-400" />

                    {isDemo && (
                        <div className="absolute -top-3 right-4 flex flex-wrap items-center gap-2 z-10" data-testid="demo-toolbar">
                            <div className="inline-flex items-center gap-0 rounded-full bg-white shadow-md border border-slate-200 overflow-hidden" data-testid="demo-scenario-group">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-700 pl-3 pr-2">Scenario</span>
                                <select
                                    value={scenario}
                                    onChange={(e) => setScenario(e.target.value)}
                                    data-testid="demo-scenario-select"
                                    title={DEMO_SCENARIOS[scenario]?.tagline}
                                    className="appearance-none bg-transparent pr-8 pl-1 py-1.5 text-xs font-semibold text-slate-900 focus:outline-none cursor-pointer"
                                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0.5rem center", backgroundSize: "0.9em" }}
                                >
                                    {Object.entries(DEMO_SCENARIOS).map(([k, v]) => (
                                        <option key={k} value={k}>{v.label}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                onClick={() => autofill(false)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-amber-400 text-slate-900 hover:bg-amber-300 shadow-md transition"
                                data-testid="demo-autofill-btn"
                                title="Fill form with selected scenario"
                            >
                                <Beaker className="w-3.5 h-3.5" /> Autofill
                            </button>
                            <button
                                type="button"
                                onClick={() => autofill(true)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 shadow-md transition"
                                data-testid="demo-autofill-jump-btn"
                                title="Fill everything and jump to the final Submit step"
                            >
                                <SkipForward className="w-3.5 h-3.5" /> + jump
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 shadow-md transition"
                                data-testid="demo-reset-btn"
                                title="Clear all fields and go back to step 1"
                            >
                                <RotateCcw className="w-3.5 h-3.5" /> Reset
                            </button>
                        </div>
                    )}
                    {/* Step indicator with colored dots */}
                    <div className="mb-10">
                        <div className="grid grid-cols-6 gap-1 sm:gap-2 mb-4">
                            {STEPS.map((s, i) => {
                                const tones = [
                                    "bg-emerald-500", "bg-amber-500", "bg-rose-500",
                                    "bg-violet-500", "bg-sky-500", "bg-orange-500",
                                ];
                                const isDone = i < step;
                                const isCurrent = i === step;
                                const isFuture = i > step;
                                return (
                                    <div key={s} className="flex flex-col items-center text-center min-w-0">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold mb-2 transition-all ${
                                            isDone ? `${tones[i]} text-white shadow-md` :
                                            isCurrent ? `${tones[i]} text-white shadow-lg ring-4 ring-offset-1 ring-${tones[i].split("-")[1]}-200 scale-110` :
                                            "bg-slate-100 text-slate-600"
                                        }`}>
                                            {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                                        </div>
                                        <div className={`text-[10px] sm:text-xs font-semibold leading-tight truncate w-full ${
                                            isCurrent ? "text-navy-900" : isFuture ? "text-slate-600" : "text-slate-600"
                                        }`}>
                                            {s}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} data-testid="form-progress" />
                        </div>
                    </div>

                    {step === 0 && (
                        <div className="space-y-6" data-testid="step-contact">
                            <div className="flex items-center gap-3 pb-2 border-b-2 border-amber-200">
                                <span className="w-1.5 h-7 rounded-full bg-amber-500" />
                                <h2 className="font-serif text-2xl text-navy-900">What can we help you with?</h2>
                            </div>
                            <div>
                                <Label className="text-base">Choose the option that fits best *</Label>
                                <Select value={form.inquiry_type} onValueChange={(v) => set("inquiry_type", v)}>
                                    <SelectTrigger className="mt-2 h-12 text-base" data-testid="select-inquiry-type">
                                        <SelectValue placeholder="Pick one — we tailor the call to your situation" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="review-existing">Review my existing annuity</SelectItem>
                                        <SelectItem value="first-purchase">First-time annuity purchase — pre-purchase guidance</SelectItem>
                                        <SelectItem value="additional-purchase">Adding another annuity to my plan</SelectItem>
                                        <SelectItem value="ira-roth-conversion">IRA → Roth conversion with no loss of account value</SelectItem>
                                        <SelectItem value="cd-replacement">Replace a CD with higher rates, more liquidity, &amp; tax deferral</SelectItem>
                                        <SelectItem value="just-questions">I just have questions — not sure yet</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="mt-2 text-xs text-slate-700">If you're not reviewing an existing contract, leave the annuity details (company, value, etc.) blank — we'll cover those on the call.</p>
                            </div>

                            <div className="flex items-center gap-3 pt-4 pb-2 border-b-2 border-emerald-100">
                                <span className="w-1.5 h-7 rounded-full bg-emerald-500" />
                                <h2 className="font-serif text-2xl text-navy-900">Your contact details</h2>
                            </div>
                            <div>
                                <Label htmlFor="full_name" className="text-base">Full Name *</Label>
                                <Input id="full_name" data-testid="input-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2 h-12 text-base" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <Label htmlFor="email" className="text-base">Email *</Label>
                                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 h-12 text-base" />
                                </div>
                                <div>
                                    <Label htmlFor="phone" className="text-base">Phone Number *</Label>
                                    <Input id="phone" type="tel" data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 h-12 text-base" />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <Label className="text-base">Age Range *</Label>
                                    <Select value={form.age} onValueChange={(v) => set("age", v)}>
                                        <SelectTrigger className="mt-2 h-12 text-base" data-testid="select-age"><SelectValue placeholder="Select age range" /></SelectTrigger>
                                        <SelectContent>{AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <Label className="text-base">State *</Label>
                                    <Select value={form.state} onValueChange={(v) => set("state", v)}>
                                        <SelectTrigger className="mt-2 h-12 text-base" data-testid="select-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                                        <SelectContent className="max-h-64">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="zip_code" className="text-base">ZIP Code</Label>
                                    <Input id="zip_code" data-testid="input-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} className="mt-2 h-12 text-base" placeholder="e.g. 33101" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-base">How did you hear about us? <span className="text-slate-600 text-sm font-normal">(optional)</span></Label>
                                <Select value={form.lead_source || ""} onValueChange={(v) => set("lead_source", v)}>
                                    <SelectTrigger className="mt-2 h-12 text-base" data-testid="select-lead-source">
                                        <SelectValue placeholder="Choose one — helps us improve" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="google">Google search</SelectItem>
                                        <SelectItem value="google-ads">Google ad</SelectItem>
                                        <SelectItem value="facebook">Facebook / Instagram</SelectItem>
                                        <SelectItem value="tiktok">TikTok</SelectItem>
                                        <SelectItem value="youtube">YouTube</SelectItem>
                                        <SelectItem value="referral">Referred by a friend</SelectItem>
                                        <SelectItem value="agent-page">An agent shared a link</SelectItem>
                                        <SelectItem value="email">Email or newsletter</SelectItem>
                                        <SelectItem value="other">Something else</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-6" data-testid="step-annuity">
                            <div className="flex items-center gap-3 pb-2 border-b-2 border-amber-100">
                                <span className="w-1.5 h-7 rounded-full bg-amber-500" />
                                <h2 className="font-serif text-2xl text-navy-900">Annuity details</h2>
                            </div>
                            <div>
                                <Label className="text-base">Annuity Type *</Label>
                                <Select value={form.annuity_type} onValueChange={(v) => set("annuity_type", v)}>
                                    <SelectTrigger className="mt-2 h-12 text-base" data-testid="select-annuity-type"><SelectValue placeholder="Select annuity type" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Fixed">Fixed</SelectItem>
                                        <SelectItem value="Fixed Indexed">Fixed Indexed</SelectItem>
                                        <SelectItem value="Variable">Variable</SelectItem>
                                        <SelectItem value="Not Sure">Not Sure</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="company" className="text-base">Insurance Company</Label>
                                <Input id="company" data-testid="input-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="e.g., Allianz, Athene, Nationwide" className="mt-2 h-12 text-base" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <Label htmlFor="current_value" className="text-base">Current Value ($)</Label>
                                    <Input id="current_value" type="number" data-testid="input-value" value={form.current_value} onChange={(e) => set("current_value", e.target.value)} placeholder="100000" className="mt-2 h-12 text-base" />
                                </div>
                                <div>
                                    <Label className="text-base">Purchase Year</Label>
                                    <Select value={form.purchase_year} onValueChange={(v) => set("purchase_year", v)}>
                                        <SelectTrigger className="mt-2 h-12 text-base" data-testid="select-year"><SelectValue placeholder="Select year" /></SelectTrigger>
                                        <SelectContent className="max-h-64">{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-8" data-testid="step-features">
                            <div className="flex items-center gap-3 pb-2 border-b-2 border-rose-100">
                                <span className="w-1.5 h-7 rounded-full bg-rose-500" />
                                <h2 className="font-serif text-2xl text-navy-900">Features and goals</h2>
                            </div>
                            <div>
                                <Label className="text-base">Income Rider</Label>
                                <RadioGroup value={form.income_rider} onValueChange={(v) => set("income_rider", v)} className="mt-3 grid grid-cols-3 gap-3" data-testid="radio-income">
                                    {["Yes", "No", "Not Sure"].map((v) => (
                                        <label key={v} className={`cursor-pointer border rounded-md px-4 py-4 text-center text-base font-medium transition-all ${form.income_rider === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 hover:border-navy-300"}`}>
                                            <RadioGroupItem value={v} className="sr-only" />
                                            {v}
                                        </label>
                                    ))}
                                </RadioGroup>
                            </div>
                            <div>
                                <Label className="text-base">Do you know your surrender charge schedule?</Label>
                                <RadioGroup value={form.surrender_charge_known} onValueChange={(v) => set("surrender_charge_known", v)} className="mt-3 grid grid-cols-2 gap-3" data-testid="radio-surrender">
                                    {["Yes", "No"].map((v) => (
                                        <label key={v} className={`cursor-pointer border rounded-md px-4 py-4 text-center text-base font-medium transition-all ${form.surrender_charge_known === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 hover:border-navy-300"}`}>
                                            <RadioGroupItem value={v} className="sr-only" />
                                            {v}
                                        </label>
                                    ))}
                                </RadioGroup>
                            </div>
                            <div>
                                <Label className="text-base">Primary Goal</Label>
                                <RadioGroup value={form.goal} onValueChange={(v) => set("goal", v)} className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="radio-goal">
                                    {["Income Now", "Income Later", "Growth", "Protection"].map((v) => (
                                        <label key={v} className={`cursor-pointer border rounded-md px-3 py-4 text-center text-sm font-medium transition-all ${form.goal === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 hover:border-navy-300"}`}>
                                            <RadioGroupItem value={v} className="sr-only" />
                                            {v}
                                        </label>
                                    ))}
                                </RadioGroup>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6" data-testid="step-needs">
                            <div>
                                <div className="flex items-center gap-3 pb-2 border-b-2 border-violet-100">
                                    <span className="w-1.5 h-7 rounded-full bg-violet-500" />
                                    <h2 className="font-serif text-2xl text-navy-900">Your retirement picture</h2>
                                </div>
                                <p className="text-slate-600 mt-2 text-base">A few quick questions so we can tell whether your annuity actually fits where you are today. No right or wrong answers.</p>
                            </div>

                            <NeedsQuestion
                                testid="income-confidence"
                                label="Do you feel you have enough guaranteed income in retirement?"
                                value={form.income_confidence}
                                onChange={(v) => set("income_confidence", v)}
                                options={["Very confident", "Mostly", "A little worried", "Not enough"]}
                            />

                            <NeedsQuestion
                                testid="ltc-concern"
                                label="How concerned are you about unexpected long-term care costs — home health, assisted living, or a nursing home?"
                                value={form.ltc_concern}
                                onChange={(v) => set("ltc_concern", v)}
                                options={["Very concerned", "Somewhat", "Already planned for it", "Not concerned"]}
                            />

                            <NeedsQuestion
                                testid="currently-taking-income"
                                label="Are you currently taking income from this annuity?"
                                value={form.currently_taking_income}
                                onChange={(v) => set("currently_taking_income", v)}
                                options={["Yes, I am", "No — plan to later", "No — probably never", "Not sure"]}
                                hint="If you've been paying for an income rider for years but will never turn it on, that's money back in your pocket."
                            />

                            <NeedsQuestion
                                testid="financial-situation-changed"
                                label="Has your financial situation changed much since you bought this annuity?"
                                value={form.financial_situation_changed}
                                onChange={(v) => set("financial_situation_changed", v)}
                                options={["A lot", "Somewhat", "Not really"]}
                                cols={3}
                            />

                            <NeedsQuestion
                                testid="marital-status"
                                label="Are you married or have a partner?"
                                value={form.marital_status}
                                onChange={(v) => {
                                    set("marital_status", v);
                                    if (v !== "Yes") set("spouse_has_annuity", "");
                                }}
                                options={["Yes", "No"]}
                                cols={2}
                            />

                            {form.marital_status === "Yes" && (
                                <NeedsQuestion
                                    testid="spouse-has-annuity"
                                    label="Does your spouse or partner also own an annuity?"
                                    value={form.spouse_has_annuity}
                                    onChange={(v) => set("spouse_has_annuity", v)}
                                    options={["Yes", "No", "Not sure"]}
                                    cols={3}
                                />
                            )}

                            <div>
                                <Label className="text-base">Anything specific you'd like us to look at?</Label>
                                <Textarea
                                    rows={3}
                                    value={form.review_focus}
                                    onChange={(e) => set("review_focus", e.target.value)}
                                    placeholder="e.g., Is the income rider fee still worth paying? Should I keep or exchange it? Does this work with my spouse's plan?"
                                    className="mt-2"
                                    data-testid="input-review-focus"
                                />
                                <div className="mt-1 text-xs text-slate-700">Optional — skip if nothing comes to mind.</div>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-6" data-testid="step-upload">
                            <div className="flex items-center gap-3 pb-2 border-b-2 border-sky-100">
                                <span className="w-1.5 h-7 rounded-full bg-sky-500" />
                                <h2 className="font-serif text-2xl text-navy-900">Upload your statement</h2>
                            </div>
                            <p className="text-slate-600 text-base">Upload your latest annuity statement (PDF or image). <span className="text-slate-700">Optional — but please have it available during your phone call so we can give you the most accurate review.</span></p>
                            <label htmlFor="file-input" className={`block border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${form.file_url ? "border-green-300 bg-green-50" : "border-slate-300 hover:border-navy-300 bg-slate-50"}`}>
                                {form.file_url ? (
                                    <div className="flex flex-col items-center gap-3" data-testid="upload-success">
                                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                                        <div className="font-medium text-slate-800 break-all">{form.file_name}</div>
                                        <div className="text-sm text-slate-700">Click to replace</div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-3">
                                        <Upload className="w-10 h-10 text-slate-600" />
                                        <div className="font-medium text-slate-700">{uploading ? "Uploading..." : "Click to upload PDF or image"}</div>
                                        <div className="text-sm text-slate-700">Max 15MB · PDF, JPG, PNG, WEBP</div>
                                    </div>
                                )}
                                <input id="file-input" type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} data-testid="input-file" />
                            </label>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-6" data-testid="step-submit">
                            <div className="flex items-center gap-3 pb-2 border-b-2 border-orange-100">
                                <span className="w-1.5 h-7 rounded-full bg-orange-500" />
                                <h2 className="font-serif text-2xl text-navy-900">Review and submit</h2>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-base">
                                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                                    <Row label="Name" value={form.full_name} />
                                    <Row label="Email" value={form.email} />
                                    <Row label="Phone" value={form.phone} />
                                    <Row label="Age" value={form.age} />
                                    <Row label="State" value={form.state} />
                                    <Row label="Annuity Type" value={form.annuity_type} />
                                    <Row label="Company" value={form.company} />
                                    <Row label="Current Value" value={form.current_value ? `$${Number(form.current_value).toLocaleString()}` : ""} />
                                    <Row label="Purchase Year" value={form.purchase_year} />
                                    <Row label="Income Rider" value={form.income_rider} />
                                    <Row label="Surrender Schedule" value={form.surrender_charge_known} />
                                    <Row label="Primary Goal" value={form.goal} />
                                    <Row label="Enough retirement income" value={form.income_confidence} />
                                    <Row label="Long-term care concern" value={form.ltc_concern} />
                                    <Row label="Currently taking income" value={form.currently_taking_income} />
                                    <Row label="Situation changed" value={form.financial_situation_changed} />
                                    <Row label="Married/partnered" value={form.marital_status} />
                                    {form.marital_status === "Yes" && <Row label="Spouse has annuity" value={form.spouse_has_annuity} />}
                                </div>
                                {form.review_focus && (
                                    <div className="mt-5 pt-5 border-t border-slate-200">
                                        <div className="text-xs uppercase tracking-[0.2em] text-slate-700 font-semibold">Focus for reviewer</div>
                                        <div className="mt-1 text-slate-700 text-sm whitespace-pre-wrap">{form.review_focus}</div>
                                    </div>
                                )}
                                {form.file_name && (
                                    <div className="mt-5 pt-5 border-t border-slate-200 flex items-center gap-3 text-slate-700">
                                        <FileText className="w-4 h-4" /> <span className="text-sm">{form.file_name}</span>
                                    </div>
                                )}
                            </div>
                            <p className="text-sm text-slate-700">By submitting, you consent to be contacted by a licensed insurance professional regarding your annuity review.</p>
                        </div>
                    )}

                    {/* Nav */}
                    <div className="mt-10 flex items-center justify-between">
                        <Button variant="outline" onClick={back} disabled={step === 0} data-testid="btn-back" className="px-6 py-5 rounded-md">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back
                        </Button>
                        {step < STEPS.length - 1 ? (
                            <Button onClick={next} data-testid="btn-next" className="bg-navy-900 hover:bg-navy-800 text-white px-8 py-5 rounded-md">
                                Continue <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={submit} disabled={submitting} data-testid="btn-submit" className="bg-navy-900 hover:bg-navy-800 text-white px-8 py-5 rounded-md">
                                {submitting ? "Submitting..." : "Submit My Review"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-slate-700">{label}</span>
            <span className="text-slate-900 font-medium text-right">{value || "—"}</span>
        </div>
    );
}

function NeedsQuestion({ testid, label, value, onChange, options, cols, hint }) {
    const colClass = cols === 2 ? "grid-cols-2" : cols === 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-4";
    return (
        <div>
            <Label className="text-base text-slate-800">{label}</Label>
            {hint && <div className="mt-1 text-xs text-slate-700">{hint}</div>}
            <RadioGroup value={value} onValueChange={onChange} className={`mt-3 grid ${colClass} gap-3`} data-testid={`radio-${testid}`}>
                {options.map((v) => (
                    <label
                        key={v}
                        className={`cursor-pointer border rounded-md px-3 py-3 text-center text-sm font-medium transition-all ${value === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 hover:border-navy-300"}`}
                    >
                        <RadioGroupItem value={v} className="sr-only" />
                        {v}
                    </label>
                ))}
            </RadioGroup>
        </div>
    );
}
